import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const CareersPage = () => {
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    coverLetter: '',
    linkedin: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const departments = [
    { id: 'all', name: 'All Positions', count: 8 },
    { id: 'engineering', name: 'Engineering', count: 3 },
    { id: 'production', name: 'Production', count: 2 },
    { id: 'quality', name: 'Quality Assurance', count: 1 },
    { id: 'sales', name: 'Sales & Marketing', count: 2 },
  ];

  const jobOpenings = [
    {
      id: 1,
      title: 'Senior Injection Molding Engineer',
      department: 'engineering',
      location: 'Southern California, USA',
      type: 'Full-time',
      experience: '5+ years',
      description: 'Lead injection molding projects from concept to production. Design and optimize mold tooling, troubleshoot production issues, and mentor junior engineers.',
      requirements: [
        'Bachelor\'s degree in Mechanical Engineering or related field',
        '5+ years of injection molding experience',
        'Proficiency in CAD software (SolidWorks, AutoCAD)',
        'Strong knowledge of thermoplastic materials',
        'Experience with mold flow analysis',
      ],
    },
    {
      id: 2,
      title: 'CNC Machinist',
      department: 'production',
      location: 'Southern California, USA',
      type: 'Full-time',
      experience: '3+ years',
      description: 'Operate and program CNC machines to produce precision mold components. Read technical drawings and maintain quality standards.',
      requirements: [
        'High school diploma or equivalent',
        '3+ years of CNC machining experience',
        'Proficiency in G-code programming',
        'Experience with 3-axis and 5-axis machines',
        'Strong attention to detail',
      ],
    },
    {
      id: 3,
      title: 'Quality Control Inspector',
      department: 'quality',
      location: 'Southern California, USA',
      type: 'Full-time',
      experience: '2+ years',
      description: 'Inspect molded parts and tooling to ensure compliance with specifications. Use precision measuring equipment and document quality metrics.',
      requirements: [
        'Associate degree or equivalent experience',
        '2+ years in quality inspection',
        'Experience with CMM and optical measurement systems',
        'Knowledge of ISO 9001 standards',
        'Strong analytical skills',
      ],
    },
    {
      id: 4,
      title: 'Blow Molding Technician',
      department: 'production',
      location: 'Southern California, USA',
      type: 'Full-time',
      experience: '2+ years',
      description: 'Set up and operate blow molding equipment. Monitor production processes and perform routine maintenance.',
      requirements: [
        'High school diploma or equivalent',
        '2+ years of blow molding experience',
        'Understanding of extrusion processes',
        'Mechanical aptitude',
        'Ability to work in shifts',
      ],
    },
    {
      id: 5,
      title: 'Tooling Design Engineer',
      department: 'engineering',
      location: 'Netherlands (Remote options available)',
      type: 'Full-time',
      experience: '4+ years',
      description: 'Design complex injection molds and tooling systems. Collaborate with customers on DFM analysis and optimize designs for manufacturability.',
      requirements: [
        'Bachelor\'s degree in Mechanical Engineering',
        '4+ years of mold design experience',
        'Expert-level CAD skills (SolidWorks preferred)',
        'Knowledge of European tooling standards',
        'Fluent in English, Dutch is a plus',
      ],
    },
    {
      id: 6,
      title: 'Sales Engineer',
      department: 'sales',
      location: 'Remote (USA or Europe)',
      type: 'Full-time',
      experience: '3+ years',
      description: 'Develop new business opportunities and maintain client relationships. Provide technical expertise during sales process and prepare quotes.',
      requirements: [
        'Bachelor\'s degree in Engineering or Business',
        '3+ years in technical sales',
        'Understanding of molding processes',
        'Excellent communication skills',
        'Willingness to travel',
      ],
    },
    {
      id: 7,
      title: '3D Printing Specialist',
      department: 'engineering',
      location: 'Southern California, USA',
      type: 'Full-time',
      experience: '2+ years',
      description: 'Operate and maintain industrial 3D printing equipment. Prepare files, optimize print parameters, and perform post-processing.',
      requirements: [
        'Associate degree or equivalent experience',
        '2+ years of additive manufacturing experience',
        'Knowledge of multiple 3D printing technologies',
        'Experience with CAD and slicing software',
        'Problem-solving mindset',
      ],
    },
    {
      id: 8,
      title: 'Digital Marketing Specialist',
      department: 'sales',
      location: 'Remote',
      type: 'Full-time',
      experience: '2+ years',
      description: 'Manage digital marketing campaigns, create content, and promote Alpine Precision Molding\'s capabilities globally.',
      requirements: [
        'Bachelor\'s degree in Marketing or related field',
        '2+ years of digital marketing experience',
        'SEO and social media expertise',
        'Content creation skills',
        'B2B marketing experience preferred',
      ],
    },
  ];

  const benefits = [
    {
      icon: 'ri-heart-pulse-line',
      title: 'Health & Wellness',
      description: 'Comprehensive medical, dental, and vision insurance for you and your family',
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Paid Time Off',
      description: 'Generous vacation policy, sick leave, and paid holidays',
    },
    {
      icon: 'ri-funds-line',
      title: 'Retirement Plans',
      description: '401(k) with company matching to secure your future',
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'Professional Development',
      description: 'Training programs, certifications, and conference attendance',
    },
    {
      icon: 'ri-team-line',
      title: 'Work-Life Balance',
      description: 'Flexible schedules and remote work options where applicable',
    },
    {
      icon: 'ri-trophy-line',
      title: 'Performance Bonuses',
      description: 'Annual bonuses based on individual and company performance',
    },
  ];

  const filteredJobs = selectedDepartment === 'all' 
    ? jobOpenings 
    : jobOpenings.filter(job => job.department === selectedDepartment);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5v91v6s8bb84h5ieor0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          position: '',
          experience: '',
          coverLetter: '',
          linkedin: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url(https://public.readdy.ai/ai/img_res/d9d1ded70e9001dc0eeb281b4afabde2.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}></div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-primary-500/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <i className="ri-briefcase-line text-primary-300"></i>
              <span className="text-sm font-medium text-primary-300">Join Our Team</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-display font-bold text-white mb-6">
              Build Your Career with Alpine Precision
            </h1>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed">
              Join a team that combines European craftsmanship with cutting-edge technology. We're looking for talented individuals who are passionate about precision manufacturing and innovation.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <a href="#openings" className="px-8 py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white font-semibold rounded-lg hover:from-primary-600 hover:to-primary-700 transition-all shadow-lg shadow-primary-500/30 whitespace-nowrap cursor-pointer">
                View Open Positions
              </a>
              <a href="#apply" className="px-8 py-3 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-lg hover:bg-white/20 transition-all border border-white/20 whitespace-nowrap cursor-pointer">
                Submit Application
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-display font-bold text-slate-900 mb-4">
              Why Choose Alpine Precision?
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              We offer more than just a job – we provide a career path with growth opportunities, competitive benefits, and a collaborative work environment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-slate-50 rounded-xl p-8 hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-display font-bold text-slate-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section id="openings" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-display font-bold text-slate-900 mb-4">
              Current Openings
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Explore opportunities across engineering, production, quality, and sales departments.
            </p>
          </div>

          {/* Department Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {departments.map((dept) => (
              <button
                key={dept.id}
                onClick={() => setSelectedDepartment(dept.id)}
                className={`px-6 py-2 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                  selectedDepartment === dept.id
                    ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg shadow-primary-500/30'
                    : 'bg-white text-slate-700 hover:bg-slate-100'
                }`}
              >
                {dept.name} ({dept.count})
              </button>
            ))}
          </div>

          {/* Job Listings */}
          <div className="space-y-6">
            {filteredJobs.map((job) => (
              <div key={job.id} className="bg-white rounded-xl p-8 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <h3 className="text-2xl font-display font-bold text-slate-900">
                        {job.title}
                      </h3>
                      <span className="px-3 py-1 bg-primary-100 text-primary-700 text-sm font-medium rounded-full">
                        {job.type}
                      </span>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 mb-4">
                      <div className="flex items-center space-x-2">
                        <i className="ri-map-pin-line text-primary-500"></i>
                        <span>{job.location}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <i className="ri-time-line text-primary-500"></i>
                        <span>{job.experience}</span>
                      </div>
                    </div>

                    <p className="text-slate-700 mb-4 leading-relaxed">
                      {job.description}
                    </p>

                    <div>
                      <h4 className="font-semibold text-slate-900 mb-2">Requirements:</h4>
                      <ul className="space-y-1">
                        {job.requirements.map((req, idx) => (
                          <li key={idx} className="flex items-start space-x-2 text-sm text-slate-600">
                            <i className="ri-checkbox-circle-fill text-primary-500 mt-0.5"></i>
                            <span>{req}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="lg:w-48 flex-shrink-0">
                    <a
                      href="#apply"
                      onClick={() => setFormData(prev => ({ ...prev, position: job.title }))}
                      className="block w-full px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white text-center font-semibold rounded-lg hover:from-primary-600 hover:to-primary-700 transition-all shadow-lg shadow-primary-500/30 whitespace-nowrap cursor-pointer"
                    >
                      Apply Now
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="apply" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-display font-bold text-slate-900 mb-4">
              Submit Your Application
            </h2>
            <p className="text-lg text-slate-600">
              Fill out the form below and we'll get back to you within 48 hours.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-slate-50 rounded-xl p-8" data-readdy-form>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-slate-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div>
                <label htmlFor="position" className="block text-sm font-medium text-slate-700 mb-2">
                  Position Applied For *
                </label>
                <select
                  id="position"
                  name="position"
                  value={formData.position}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="">Select a position</option>
                  {jobOpenings.map((job) => (
                    <option key={job.id} value={job.title}>
                      {job.title}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="experience" className="block text-sm font-medium text-slate-700 mb-2">
                  Years of Experience *
                </label>
                <select
                  id="experience"
                  name="experience"
                  value={formData.experience}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="">Select experience level</option>
                  <option value="0-2">0-2 years</option>
                  <option value="2-5">2-5 years</option>
                  <option value="5-10">5-10 years</option>
                  <option value="10+">10+ years</option>
                </select>
              </div>

              <div>
                <label htmlFor="linkedin" className="block text-sm font-medium text-slate-700 mb-2">
                  LinkedIn Profile
                </label>
                <input
                  type="url"
                  id="linkedin"
                  name="linkedin"
                  value={formData.linkedin}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  placeholder="https://linkedin.com/in/yourprofile"
                />
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="coverLetter" className="block text-sm font-medium text-slate-700 mb-2">
                Cover Letter / Why You're Interested *
              </label>
              <textarea
                id="coverLetter"
                name="coverLetter"
                value={formData.coverLetter}
                onChange={handleInputChange}
                required
                maxLength={500}
                rows={6}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm resize-none"
                placeholder="Tell us why you're interested in this position and what makes you a great fit..."
              ></textarea>
              <p className="text-xs text-slate-500 mt-1">
                {formData.coverLetter.length}/500 characters
              </p>
            </div>

            <div className="mb-6">
              <p className="text-sm text-slate-600 bg-amber-50 border border-amber-200 rounded-lg p-4">
                <i className="ri-information-line text-amber-600 mr-2"></i>
                Please note: Resume upload is not available in this form. You can include a link to your resume in your cover letter or send it directly to careers@alpineprecisionmold.com
              </p>
            </div>

            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center space-x-2 text-green-800">
                  <i className="ri-checkbox-circle-fill text-xl"></i>
                  <span className="font-medium">Application submitted successfully! We'll be in touch soon.</span>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center space-x-2 text-red-800">
                  <i className="ri-error-warning-fill text-xl"></i>
                  <span className="font-medium">Something went wrong. Please try again or email us directly.</span>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-8 py-4 bg-gradient-to-r from-primary-500 to-primary-600 text-white font-semibold rounded-lg hover:from-primary-600 hover:to-primary-700 transition-all shadow-lg shadow-primary-500/30 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center space-x-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  <span>Submitting...</span>
                </span>
              ) : (
                'Submit Application'
              )}
            </button>
          </form>
        </div>
      </section>

      {/* Culture Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-display font-bold text-slate-900 mb-6">
                Our Culture & Values
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                At Alpine Precision Molding, we believe in fostering a culture of innovation, collaboration, and continuous improvement. Our team members are our greatest asset, and we invest in their growth and success.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-lightbulb-line text-primary-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Innovation First</h3>
                    <p className="text-slate-600 text-sm">We encourage creative thinking and embrace new technologies to stay ahead.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-team-line text-primary-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Collaborative Environment</h3>
                    <p className="text-slate-600 text-sm">Cross-functional teamwork drives our success and creates learning opportunities.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-star-line text-primary-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Excellence in Everything</h3>
                    <p className="text-slate-600 text-sm">We maintain the highest standards in quality, service, and craftsmanship.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="https://readdy.ai/api/search-image?query=diverse%20team%20of%20engineers%20and%20technicians%20collaborating%20in%20modern%20manufacturing%20facility%20professional%20workplace%20with%20people%20working%20together%20on%20precision%20equipment%20bright%20clean%20industrial%20environment%20showing%20teamwork%20and%20innovation&width=800&height=600&seq=careers-culture-001&orientation=landscape"
                  alt="Team collaboration"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CareersPage;
